import * as React from 'react';
import classNames from 'classnames';
import Icon from '@/components/ui/Icon';
import Tooltip from '@/components/ui/Tooltip';
import styles from '@/components/ui/ListMissions/styles.module.scss';
import Image from 'next/image';
import Link from 'next/link';
import Progressbar from '@/components/ui/Progressbar';
import { clamp } from '@/components/helpers/clamp';
import { LinkProps } from 'next/dist/client/link';

export type IMissionLayout = 'default' | 'tickets';
export type IMissionTheme = 'gold' | 'silver' | 'bronze';

export interface IListMissionsItem {
  progress: number | null;
  progressLabel: string | null;
  completed: boolean;
  repetition: number | null;
  recurring: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
  image?: {
    src: string;
    alt: string;
  };
  backgroundImage?: string;
  tooltip?: React.ReactNode;
  href?: LinkProps['href'];
  layout?: IMissionLayout;
  theme?: IMissionTheme;
  badgeLabel?: string;
  button?: React.ReactNode;
  buttonDescription?: React.ReactNode;
  callout?: React.ReactNode;
  tickets?: {
    imageUrl: string;
    label: React.ReactNode;
    amount: React.ReactNode;
  };
  tag?: {
    label: string;
    intent: 'success' | 'danger';
  };
  discount?: React.ReactNode;
  highlight?: React.ReactNode;
  selected?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  price?: React.ReactNode;
}

const ListMissionsItem: React.FunctionComponent<IListMissionsItem> = (props) => {
  // TODO i18n
  const labels = {
    completed: 'Completed',
    worth: 'Worth',
  };

  return (
    <li>
      <div
        className={classNames(styles['list-missions-item'], {
          [styles['has-theme']]: props.theme || props.completed,
          [styles['theme-1']]: props.theme === 'gold',
          [styles['theme-2']]: props.theme === 'silver',
          [styles['theme-3']]: props.theme === 'bronze',
          [styles['theme-completed']]: props.completed,
          [styles['layout-default']]: props.layout === 'default' || !props.layout,
          [styles['layout-tickets']]: props.layout === 'tickets',
          [styles['is-clickable']]: props.onClick,
        })}
        onClick={props.onClick}
        aria-current={props.selected === true ? 'true' : 'false'}
      >
        {!props.onClick && props.highlight && (
          <div className={styles['list-missions-item__highlight']}>
            <div className={styles['list-missions-item__highlight__label']}>
              <div>{props.highlight}</div>
              <Icon
                width={14}
                height={14}
                kind="arrow02-down"
              />
            </div>
          </div>
        )}
        {props.onClick && <div className={styles['list-missions-item__clicker']} />}
        {props.tooltip && <Tooltip>{props.tooltip}</Tooltip>}
        {props.tag && (
          <div className={styles['list-missions-item__tag']}>
            <div
              className={classNames(styles['list-missions-item__tag__label'], {
                [styles['intent-danger']]: props.tag.intent === 'danger',
                [styles['intent-success']]: props.tag.intent === 'success',
              })}
            >
              <div>{props.tag.label}</div>
            </div>
          </div>
        )}
        <div className={styles['list-missions-item__bg']}>
          <div className={styles['list-missions-item__bg__inner']} />
          {props.backgroundImage && (
            <div className={styles['list-missions-item__bg__image']}>
              <Image
                src={props.backgroundImage}
                alt=""
                width={300}
                height={500}
              />
            </div>
          )}
        </div>
        {props.badgeLabel && (
          <div className={styles['list-missions-item__badge']}>
            <div>{props.badgeLabel}</div>
          </div>
        )}
        {props.completed && (
          <Icon
            width={32}
            height={32}
            kind="completed-decorated"
            wrapperClassName={styles['list-missions-item__completed-mobile']}
          />
        )}
        <div className={styles['list-missions-item__main']}>
          {props.href && (
            <Link
              className={styles['list-missions-item__main__link']}
              href={props.href}
              draggable={false}
              prefetch={false}
            />
          )}
          <div className={styles['list-missions-item__header']}>
            {props.title && <div className={styles['list-missions-item__header__title']}>{props.title}</div>}
            {props.description && <div className={styles['list-missions-item__header__description']}>{props.description}</div>}
            {props.layout !== 'tickets' && props.tickets && (
              <div className={styles['list-missions-item__tickets']}>
                <div className={styles['list-missions-item__tickets__image']}>
                  <Image
                    src={props.tickets.imageUrl}
                    alt=""
                    width={18}
                    height={18}
                  />
                </div>
                <div className={styles['list-missions-item__tickets__amount']}>{props.tickets.amount}</div>
                <div className={styles['list-missions-item__tickets__label']}>{props.tickets.label}</div>
              </div>
            )}
            {props.layout !== 'tickets' && props.discount && <div className={styles['list-missions-item__discount']}>{props.discount}</div>}
          </div>
          {props.layout === 'tickets' && props.tickets && (
            <div className={styles['list-missions-item__tickets-large']}>
              {props.discount && (
                <div
                  className={classNames(
                    styles['list-missions-item__tickets-large__discount'],
                    styles['list-missions-item__discount-badge'],
                  )}
                >
                  {props.discount}
                </div>
              )}
              <div className={styles['list-missions-item__tickets-large__inner']}>
                <div className={styles['list-missions-item__tickets-large__image']}>
                  <Image
                    src={props.tickets.imageUrl}
                    alt=""
                    fill={true}
                    sizes="32px"
                  />
                </div>
                <div className={styles['list-missions-item__tickets-large__amount']}>{props.tickets.amount}</div>
                <div className={styles['list-missions-item__tickets-large__label']}>{props.tickets.label}</div>
              </div>
            </div>
          )}
          <div className={styles['list-missions-item__center-items']}>
            {props.image && (
              <div className={styles['list-missions-item__image']}>
                <div className={styles['list-missions-item__image__inner']}>
                  <Image
                    src={props.image.src}
                    alt={props.image.alt}
                    fill={true}
                    sizes="140px"
                  />
                </div>
              </div>
            )}
            {props.completed && (
              <div className={styles['list-missions-item__icon']}>
                <div className={styles['list-missions-item__icon__icon']}>
                  <Icon
                    width={64}
                    height={64}
                    kind="completed-decorated"
                  />
                </div>
                {props.button && (
                  <div className={styles['list-missions-item__icon__completed']}>
                    <span>{labels.completed}</span>
                    {props.repetition !== null && <b>{props.repetition}x</b>}
                  </div>
                )}
              </div>
            )}
          </div>
          {props.highlight && props.onClick && props.layout === 'tickets' && (
            <div className={styles['list-missions-item__highlight-inline']}>{props.highlight}</div>
          )}
          {props.callout}
        </div>
        <div className={styles['list-missions-item__tools']}>
          <div className={styles['list-missions-item__tools__inner']}>
            {(props.title || props.description) && (
              <div className={styles['list-missions-item__header-mobile']}>
                {props.title && <div className={styles['list-missions-item__header-mobile__title']}>{props.title}</div>}
                {props.description && <div className={styles['list-missions-item__header-mobile__description']}>{props.description}</div>}
              </div>
            )}
            <ul className={styles['list-missions-item__stats']}>
              {props.repetition !== null && !props.completed && (
                <li className={styles['repetition']}>
                  <span>{labels.completed}</span>
                  <b>{props.repetition}x</b>
                </li>
              )}
              {props.repetition === null && !props.completed && props.button && props.progressLabel && (
                <li className={classNames(styles['repetition'], styles['important'])}>
                  <span>{labels.completed}</span>
                  <b>{props.progressLabel}</b>
                </li>
              )}
            </ul>
            {props.completed ? (
              <React.Fragment>
                {!props.button && (
                  <div className={styles['list-missions-item__completed']}>
                    <span>{labels.completed}</span>
                    {props.repetition !== null && <b>{props.repetition}x</b>}
                  </div>
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                {typeof props.progress === 'number' && !props.button && (
                  <React.Fragment>
                    <Progressbar value={clamp(props.progress, 0, 100)} />
                    {props.progressLabel && <div className={styles['list-missions-item__progress-label']}>{props.progressLabel}</div>}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            {props.layout === 'tickets' && props.discount && (
              <div className={classNames(styles['list-missions-item__discount'], styles['list-missions-item__discount-badge'])}>
                {props.discount}
              </div>
            )}
            {props.price && <div className={styles['list-missions-item__price']}>{props.price}</div>}
            {props.button}
            {props.buttonDescription && <div className={styles['list-missions-item__button-description']}>{props.buttonDescription}</div>}
            {(props.repetition !== null || props.callout) && (
              <div className={styles['list-missions-item__tools-mobile']}>
                {props.repetition !== null && (
                  <div className={styles['list-missions-item__repetition']}>
                    <Icon
                      width={16}
                      height={16}
                      kind="repetition"
                    />
                    <span>{props.repetition}x</span>
                  </div>
                )}
                {props.callout}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

ListMissionsItem.displayName = 'ListMissionsItem';

export default ListMissionsItem;
